import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const emptyFilters = { q: '', from: '', to: '' };

const localDayStart = (day) => (day ? new Date(`${day}T00:00:00`).toISOString() : undefined);
const localDayEnd = (day) => (day ? new Date(`${day}T23:59:59.999`).toISOString() : undefined);

const filterParams = (filters) => ({
  q: filters.q || undefined,
  from: localDayStart(filters.from),
  to: localDayEnd(filters.to),
});

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function detailText(detail) {
  if (!detail || typeof detail !== 'object') return '';
  if (Array.isArray(detail) && detail.length === 0) return '';
  if (!Array.isArray(detail) && Object.keys(detail).length === 0) return '';
  return JSON.stringify(detail, null, 2);
}

export default function AuditTrailSettings() {
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState({ ...emptyFilters, page: 1 });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${API_URL}/api/audit-log`, {
          params: {
            ...filterParams(applied),
            page: applied.page,
            limit: 50,
          },
          withCredentials: true,
        });
        if (cancelled) return;
        const visible = (response.data?.data || []).filter(
          (row) => String(row.actor_role || '').toLowerCase() !== 'superadmin'
        );
        setRows(visible);
        setPagination(response.data?.pagination || { page: 1, limit: 50, total: visible.length, totalPages: 1 });
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not load the audit trail');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applied]);

  const applySearch = (event) => {
    event?.preventDefault();
    setNotice('');
    setApplied({
      q: draft.q.trim(),
      from: draft.from,
      to: draft.to,
      page: 1,
    });
  };

  const download = async (format) => {
    setExporting(format);
    setError('');
    setNotice('');
    try {
      const response = await axios.get(`${API_URL}/api/audit-log/export`, {
        params: {
          format,
          ...filterParams(applied),
        },
        withCredentials: true,
        responseType: 'blob',
      });
      const filename = format === 'json'
        ? `audit-trail-${new Date().toISOString().slice(0, 10)}.json`
        : `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      if (String(response.headers['x-audit-truncated']).toLowerCase() === 'true') {
        setNotice('The export was capped at 50,000 rows. Narrow the dates to download the rest.');
      }
    } catch (err) {
      let message = 'Could not download the audit trail';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.message || message;
        } catch (_) {
          /* keep the fallback message */
        }
      } else if (data?.message) {
        message = data.message;
      }
      setError(message);
    } finally {
      setExporting('');
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
        Audit Trail
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mb: 2 }}>
        Signed-in activity across the app: sign-ins, changes, live controls and integration writes.
        Superadmin actions are not included here or in downloads.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}

      <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 2 }}>
        <CardContent>
          <Box
            component="form"
            onSubmit={applySearch}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}
          >
            <TextField
              label="Search"
              placeholder="User, action, path or detail"
              value={draft.q}
              onChange={(event) => setDraft((current) => ({ ...current, q: event.target.value }))}
              size="small"
              sx={{ minWidth: 260, flex: 1 }}
            />
            <TextField
              label="From"
              type="date"
              value={draft.from}
              onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={draft.to}
              onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Button type="submit" variant="contained" sx={{ bgcolor: '#22d3ee', color: '#062028' }}>
              Search
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                setDraft(emptyFilters);
                setApplied({ ...emptyFilters, page: 1 });
              }}
              sx={{ borderColor: 'var(--rs-border)', color: 'var(--rs-text)' }}
            >
              Clear
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={exporting === 'csv' ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={Boolean(exporting)}
              onClick={() => download('csv')}
              sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
            >
              Download CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={exporting === 'json' ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={Boolean(exporting)}
              onClick={() => download('json')}
              sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
            >
              Download JSON
            </Button>
            <Typography variant="body2" sx={{ color: 'var(--rs-muted)', alignSelf: 'center' }}>
              {pagination.total} matching {pagination.total === 1 ? 'event' : 'events'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <TableContainer sx={{ border: '1px solid var(--rs-border)', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['When', 'Who', 'Role', 'Action', 'What happened', 'Status'].map((heading) => (
                <TableCell key={heading} sx={{ color: '#22d3ee', fontWeight: 600 }}>
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: 'var(--rs-muted)' }}>
                  Loading audit trail…
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: 'var(--rs-muted)' }}>
                  No activity matches this search.
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((row) => {
              const extra = detailText(row.detail);
              const open = openId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    hover
                    onClick={() => setOpenId(open ? null : row.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ color: 'var(--rs-text)', whiteSpace: 'nowrap' }}>
                      {formatWhen(row.occurred_at)}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--rs-text)' }}>{row.actor_username || 'Unknown'}</TableCell>
                    <TableCell sx={{ color: 'var(--rs-text)' }}>{row.actor_role || '—'}</TableCell>
                    <TableCell sx={{ color: 'var(--rs-text)' }}>{row.action}</TableCell>
                    <TableCell sx={{ color: 'var(--rs-text)' }}>{row.summary}</TableCell>
                    <TableCell sx={{ color: row.status_code >= 400 ? '#f87171' : 'var(--rs-text)' }}>
                      {row.status_code || '—'}
                    </TableCell>
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ bgcolor: 'var(--rs-bg)', color: 'var(--rs-muted)' }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {row.method || '—'} {row.path || ''}
                          {row.ip ? ` · ${row.ip}` : ''}
                        </Typography>
                        {extra && (
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              whiteSpace: 'pre-wrap',
                              fontSize: 12,
                              color: 'var(--rs-text)',
                            }}
                          >
                            {extra}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Button
          disabled={loading || applied.page <= 1}
          onClick={() => setApplied((current) => ({ ...current, page: current.page - 1 }))}
          sx={{ color: 'var(--rs-text)' }}
        >
          Previous
        </Button>
        <Typography variant="body2" sx={{ color: 'var(--rs-muted)' }}>
          Page {pagination.page || applied.page} of {pagination.totalPages || 1}
        </Typography>
        <Button
          disabled={loading || applied.page >= (pagination.totalPages || 1)}
          onClick={() => setApplied((current) => ({ ...current, page: current.page + 1 }))}
          sx={{ color: 'var(--rs-text)' }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}
